/**
 * Pure rules for cancelling and editing an order from the Orders drawer.
 *
 * Validation here only guides staff. The server stays the authority: it re-reads
 * the catalog price, validates any override, recalculates every total and decides
 * whether stock can be reserved.
 *
 * A2: an order priced by the shipping policy (`shippingFeePolicy` set) never sends a
 * plain fee. The server reprices it when products or the country change; a different
 * fee is a manager override with a reason. Orders from before A2 (no policy) keep the
 * manual fee and its OD-1 confirmation exactly as before.
 */
import {
  effectiveShippingFee,
  policyShippingFee,
  shippingFeeOverridePayload,
  validateShippingFeeOverride,
} from "./shippingPolicy";

export const CANCELLATION_REASONS = [
  { value: "CUSTOMER_CANCELLED", labelKey: "storefrontOrders.reasonCustomerCancelled" },
  { value: "CUSTOMER_UNREACHABLE", labelKey: "storefrontOrders.reasonCustomerUnreachable" },
  { value: "INVALID_DATA", labelKey: "storefrontOrders.reasonInvalidData" },
  { value: "STOCK_ISSUE", labelKey: "storefrontOrders.reasonStockIssue" },
  { value: "OTHER", labelKey: "storefrontOrders.reasonOther" },
];

/** Parses a money input ("110", "110,5"). Empty -> null, garbage -> NaN. */
export function parseAmount(value) {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const parsed = Number(String(value).trim().replace(",", "."));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : Number.NaN;
}

export function validateCancellation({ reason, note }) {
  return {
    reason: reason ? null : "storefrontOrders.cancelReasonRequired",
    note:
      reason === "OTHER" && !String(note || "").trim()
        ? "storefrontOrders.noteRequiredForOther"
        : null,
  };
}

export function buildCancellationPayload(orderId, { reason, note }) {
  const trimmedNote = String(note || "").trim();

  return {
    orderId,
    reason,
    ...(trimmedNote ? { note: trimmedNote } : {}),
  };
}

const lineOriginalPrice = (line) =>
  Number(line.originalUnitPriceAmount ?? line.finalUnitPriceAmount ?? 0);

export function buildEditDraft(order) {
  return {
    recipientName: order?.recipientName || "",
    phone: order?.phone || "",
    country: order?.country || "Kosove",
    city: order?.city || "",
    address: order?.address || "",
    shippingFeeAmount: String(Number(order?.shippingFeeAmount ?? 0)),
    // OD-1 (CMS-1): an explicit confirmation of the fee, never ticked by default.
    shippingFeeConfirmed: false,
    policyPriced: Boolean(order?.shippingFeePolicy),
    hadFeeOverride: Boolean(order?.shippingFeePolicy && order?.shippingFeeOverrideReason),
    // A saved override stays visible and is sent again with every save until staff untick it.
    feeOverride: {
      enabled: Boolean(order?.shippingFeePolicy && order?.shippingFeeOverrideReason),
      amount: order?.shippingFeeOverrideReason ? String(Number(order?.shippingFeeAmount ?? 0)) : "",
      reason: order?.shippingFeeOverrideReason || "",
    },
    lines: (order?.lines || []).map((line) => {
      const originalUnitPrice = lineOriginalPrice(line);
      const currentFinal = Number(line.finalUnitPriceAmount ?? originalUnitPrice);

      return {
        lineId: line.id,
        catalogVariantId: line.catalogVariantId ?? null,
        productTitle: line.productTitle,
        optionSummary: line.optionSummary || line.variantTitle || null,
        imageUrl: line.thumbnailUrl || line.imageUrl || null,
        currencyCode: line.currencyCode,
        quantity: line.quantity,
        originalQuantity: line.quantity,
        originalUnitPrice,
        initialFinalUnitPrice: currentFinal,
        // Unchanged, the final price defaults to what the order already charges.
        finalUnitPrice: String(currentFinal),
        // Until live stock is known the quantity can only go down.
        maxQuantity: line.quantity,
      };
    }),
  };
}

/** Legacy lines have no catalog variant and cannot be re-reserved or re-priced. */
export function canEditOrderProducts(draft) {
  return Boolean(draft?.lines?.length) && draft.lines.every((line) => line.catalogVariantId != null);
}

export function validateEditDraft(draft) {
  const required = (value, key) => (String(value || "").trim() ? null : key);
  const shipping = parseAmount(draft.shippingFeeAmount);

  const override = draft.policyPriced ? validateShippingFeeOverride(draft.feeOverride || {}) : { amount: null, reason: null };
  const errors = {
    recipientName: required(draft.recipientName, "ordersWizard.nameRequired"),
    phone: required(draft.phone, "ordersWizard.phoneRequired"),
    city: required(draft.city, "ordersWizard.cityRequired"),
    address: required(draft.address, "ordersWizard.addressRequired"),
    shippingFeeAmount:
      !draft.policyPriced && (shipping === null || Number.isNaN(shipping) || shipping < 0)
        ? "storefrontOrders.shippingFeeInvalid"
        : null,
    country:
      draft.policyPriced && !draft.feeOverride?.enabled && policyShippingFee(draft.country, 0) === null
        ? "storefrontOrders.shippingCountryUnsupported"
        : null,
    feeOverrideAmount: override.amount,
    feeOverrideReason: override.reason,
    lines: {},
  };

  if (!canEditOrderProducts(draft)) {
    return errors;
  }

  for (const line of draft.lines) {
    const quantity = Number(line.quantity);
    const finalPrice = parseAmount(line.finalUnitPrice);
    const lineErrors = {};

    if (!Number.isInteger(quantity) || quantity < 1) {
      lineErrors.quantity = "storefrontOrders.quantityInvalid";
    } else if (quantity > Number(line.maxQuantity || 0)) {
      lineErrors.quantity = "storefrontOrders.quantityAboveStock";
    }

    if (finalPrice === null) {
      lineErrors.finalUnitPrice = "storefrontOrders.finalPriceRequired";
    } else if (Number.isNaN(finalPrice) || finalPrice < 0) {
      lineErrors.finalUnitPrice = "storefrontOrders.finalPriceInvalid";
    } else if (finalPrice > line.originalUnitPrice) {
      lineErrors.finalUnitPrice = "storefrontOrders.finalPriceAboveOriginal";
    }

    if (Object.keys(lineErrors).length) {
      errors.lines[line.lineId] = lineErrors;
    }
  }

  return errors;
}

export function hasEditErrors(errors) {
  const { lines, ...fields } = errors;
  return Object.values(fields).some(Boolean) || Object.keys(lines || {}).length > 0;
}

/** Preview only: the saved order shows the server-calculated totals. */
export function previewEditTotals(draft) {
  const subtotal = canEditOrderProducts(draft)
    ? draft.lines.reduce(
        (sum, line) => sum + (parseAmount(line.finalUnitPrice) || 0) * Number(line.quantity || 0),
        0,
      )
    : draft.lines.reduce((sum, line) => sum + line.initialFinalUnitPrice * line.originalQuantity, 0);
  const shipping = draft.policyPriced
    ? effectiveShippingFee({ country: draft.country, itemsTotal: Math.round(subtotal * 100) / 100, override: draft.feeOverride })
    : Math.max(0, parseAmount(draft.shippingFeeAmount) || 0);

  return {
    subtotal: Math.round(subtotal * 100) / 100,
    shipping,
    cod: Math.round((subtotal + shipping) * 100) / 100,
  };
}

/**
 * PATCH body for the edit. Products are only sent when a quantity or final price
 * actually changed, so a recipient-only edit never touches reservations. A final
 * price equal to the original is sent as "no override".
 */
export function buildOrderUpdatePayload(order, draft) {
  const payload = {
    orderId: order.id,
    recipientName: draft.recipientName.trim(),
    phone: draft.phone.trim(),
    country: draft.country,
    city: draft.city.trim(),
    address: draft.address.trim(),
  };

  if (draft.policyPriced) {
    if (draft.feeOverride?.enabled) {
      payload.shippingFeeOverride = shippingFeeOverridePayload(draft.feeOverride);
    } else if (draft.hadFeeOverride) {
      // Unticking a saved override returns the order to the policy fee.
      payload.shippingFeeOverride = null;
    }
  } else {
    payload.shippingFeeAmount = parseAmount(draft.shippingFeeAmount);
    // OD-1: only an explicit tick confirms the fee (any amount, including 0). The amount
    // above is sent on every edit, so it never counts as a confirmation by itself.
    if (draft.shippingFeeConfirmed === true) {
      payload.shippingFeeConfirmed = true;
    }
  }

  const productsChanged =
    canEditOrderProducts(draft)
    && draft.lines.some(
      (line) =>
        Number(line.quantity) !== line.originalQuantity
        || parseAmount(line.finalUnitPrice) !== line.initialFinalUnitPrice,
    );

  if (productsChanged) {
    payload.products = draft.lines.map((line) => {
      const finalPrice = parseAmount(line.finalUnitPrice);

      return {
        catalogVariantId: line.catalogVariantId,
        quantity: Number(line.quantity),
        ...(finalPrice !== line.originalUnitPrice ? { finalUnitPriceAmount: finalPrice } : {}),
      };
    });
  }

  return payload;
}
